/**
 * Customer dashboard pages — barrel + CustomerAccountPage host.
 *
 * Refactored 22 May 2026 (P3): split smaller pages into /pages/customer/.
 * CustomerPages.jsx now contains only CustomerAccountPage (660+ lines, tightly
 * coupled to 5 tabs of internal state) and re-exports the rest.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navigation } from "../components/Navigation";
import { User, ShoppingBag, MapPin, Lock, Heart, Trash2, LogOut, Gift, Copy, Check } from "lucide-react";
import { OrderDetailModal } from "./customer/OrderDetailModal";
import { useNoindex } from "../hooks/useNoindex";

// Re-exports for backwards compatibility with /app/frontend/src/App.js
export { CustomerLoginPage } from "./customer/CustomerLoginPage";
export { CustomerRegisterPage } from "./customer/CustomerRegisterPage";
export { ForgotPasswordPage } from "./customer/ForgotPasswordPage";
export { ResetPasswordPage } from "./customer/ResetPasswordPage";
export { OrderDetailModal } from "./customer/OrderDetailModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// ============== CUSTOMER ACCOUNT DASHBOARD ==============

export const CustomerAccountPage = () => {
  useNoindex();
  const { user, logout, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('profile'); // profile | address | orders | password | wishlist | referral
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  // Address book modal state
  const [addressForm, setAddressForm] = useState(null); // null = closed, {} = new, {...} = edit
  const blankAddress = { label: "", full_name: "", phone: "", address: "", city: "", zip_code: "", province: "", country: "Italia", is_default: false };

  useEffect(() => {
    document.title = "Il mio account | Edicola Cerasuolo";
    // Allow deep-linking to specific tab via ?tab=wishlist
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (t && ['profile','address','orders','password','wishlist','referral'].includes(t)) setActiveTab(t);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'customer') {
      window.location.href = '/login';
      return;
    }
    const fetchData = async () => {
      try {
        const [pRes, oRes, wRes, aRes, rRes] = await Promise.all([
          axios.get(`${API}/customer/profile`),
          axios.get(`${API}/customer/orders`),
          axios.get(`${API}/customer/wishlist`),
          axios.get(`${API}/customer/addresses`),
          axios.get(`${API}/customer/referral`).catch(() => ({ data: null })),
        ]);
        setProfile(pRes.data);
        setOrders(oRes.data || []);
        setWishlist(wRes.data || []);
        setAddresses(aRes.data || []);
        setReferralData(rRes.data);
      } catch (e) {
        // If 401, redirect to login
        if (e.response?.status === 401) {
          logout();
          window.location.href = '/login';
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, authLoading]);

  const removeFromWishlist = async (articleId) => {
    try {
      await axios.delete(`${API}/customer/wishlist/${articleId}`);
      setWishlist((prev) => prev.filter((w) => w.article_id !== articleId));
    } catch { /* ignore */ }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setMessage(""); setError("");
    setSaving(true);
    try {
      const data = { ...addressForm };
      if (data.id) {
        // Update
        const res = await axios.put(`${API}/customer/addresses/${data.id}`, data);
        setAddresses(prev => prev.map(a => a.id === data.id ? res.data : (res.data.is_default ? { ...a, is_default: false } : a)));
        setMessage("Indirizzo aggiornato");
      } else {
        // Create
        const res = await axios.post(`${API}/customer/addresses`, data);
        setAddresses(prev => (res.data.is_default ? prev.map(a => ({ ...a, is_default: false })) : prev).concat(res.data));
        setMessage("Indirizzo aggiunto");
      }
      setAddressForm(null);
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Errore salvataggio indirizzo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Eliminare questo indirizzo?")) return;
    try {
      await axios.delete(`${API}/customer/addresses/${id}`);
      const fresh = await axios.get(`${API}/customer/addresses`);
      setAddresses(fresh.data || []);
      setMessage("Indirizzo eliminato");
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`${API}/customer/addresses/${id}`, { is_default: true });
      const fresh = await axios.get(`${API}/customer/addresses`);
      setAddresses(fresh.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore');
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setMessage(""); setError("");
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customer/profile`, {
        full_name: profile.full_name,
        phone: profile.phone,
      });
      setProfile(res.data);
      setMessage("Profilo aggiornato con successo");
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center text-gray-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Ciao, {profile.full_name?.split(' ')[0] || 'Cliente'}</h1>
            <p className="text-gray-500 mt-1 text-sm">{profile.email}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href="/prodotti"
              data-testid="account-back-to-shop-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg transition text-sm"
            >
              <ShoppingBag className="w-4 h-4" /> Vai allo Shop
            </a>
            <button
              onClick={handleLogout}
              data-testid="account-logout-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#12121a] border border-gray-800 hover:border-red-500/40 rounded-lg text-gray-400 hover:text-red-400 transition text-sm"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <aside className="md:col-span-1">
            <nav className="bg-[#12121a] border border-gray-800 rounded-xl p-2 space-y-1">
              {[
                { id: 'profile', label: 'Dati personali', icon: User },
                { id: 'address', label: `Indirizzi${addresses.length ? ` (${addresses.length})` : ''}`, icon: MapPin },
                { id: 'orders', label: `I miei ordini${orders.length ? ` (${orders.length})` : ''}`, icon: ShoppingBag },
                { id: 'wishlist', label: `Preferiti${wishlist.length ? ` (${wishlist.length})` : ''}`, icon: Heart },
                { id: 'referral', label: `Invita amici${referralData?.referral_count ? ` (${referralData.referral_count})` : ''}`, icon: Gift },
                { id: 'password', label: 'Cambia password', icon: Lock },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMessage(""); setError(""); }}
                    data-testid={`account-tab-${tab.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition ${
                      activeTab === tab.id ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'text-gray-400 hover:text-white hover:bg-[#0a0a0f]'
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {tab.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main panel */}
          <main className="md:col-span-3">
            {message && (
              <div data-testid="account-success-msg" className="mb-4 bg-green-500/10 border border-green-500/30 text-green-400 text-sm rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                <span>{message}</span>
                <a
                  href="/prodotti"
                  data-testid="account-success-shop-cta"
                  className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-3 py-1.5 rounded text-xs whitespace-nowrap"
                >
                  🛒 Vai ai Prodotti →
                </a>
              </div>
            )}
            {error && (
              <div data-testid="account-error-msg" className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3">
                {error}
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Dati personali</h2>
                <form onSubmit={handleProfileSave} className="space-y-4" data-testid="profile-form">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Email</label>
                    <input type="email" value={profile.email || ''} disabled className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-800 rounded-lg text-gray-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Nome e Cognome</label>
                    <input
                      type="text"
                      data-testid="profile-name-input"
                      value={profile.full_name || ''}
                      onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Telefono</label>
                    <input
                      type="tel"
                      data-testid="profile-phone-input"
                      value={profile.phone || ''}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                  <button type="submit" disabled={saving} data-testid="profile-save-btn" className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg disabled:opacity-60">
                    {saving ? 'Salvataggio...' : 'Salva modifiche'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'address' && (
              <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6" data-testid="address-book-section">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="text-xl font-semibold text-white">I miei indirizzi</h2>
                    <p className="text-gray-500 text-sm mt-1">L'indirizzo predefinito viene usato al checkout.</p>
                  </div>
                  <button
                    onClick={() => setAddressForm({ ...blankAddress })}
                    data-testid="add-address-btn"
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg text-sm"
                  >
                    + Aggiungi indirizzo
                  </button>
                </div>

                {addresses.length === 0 ? (
                  <div className="text-center py-10 text-gray-500" data-testid="addresses-empty">
                    <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-700" />
                    Non hai ancora salvato indirizzi.
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2" data-testid="address-list">
                    {addresses.map((a) => (
                      <div
                        key={a.id}
                        data-testid={`address-card-${a.id}`}
                        className={`bg-[#0a0a0f] border rounded-lg p-4 ${a.is_default ? 'border-amber-500/40' : 'border-gray-800'}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-amber-400" />
                            <span className="font-semibold text-white">{a.label}</span>
                            {a.is_default && (
                              <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">Predefinito</span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-300 leading-relaxed">
                          <div>{a.full_name}</div>
                          <div>{a.address}</div>
                          <div>{a.zip_code} {a.city} {a.province && `(${a.province})`}</div>
                          <div className="text-gray-500">{a.country}</div>
                          {a.phone && <div className="text-gray-500 text-xs mt-1">{a.phone}</div>}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-800 flex gap-2 flex-wrap">
                          {!a.is_default && (
                            <button
                              onClick={() => handleSetDefault(a.id)}
                              data-testid={`set-default-${a.id}`}
                              className="text-xs px-2.5 py-1 bg-[#12121a] border border-gray-700 hover:border-amber-500/40 text-gray-400 hover:text-amber-400 rounded transition"
                            >
                              Imposta predefinito
                            </button>
                          )}
                          <button
                            onClick={() => setAddressForm({ ...a })}
                            data-testid={`edit-address-${a.id}`}
                            className="text-xs px-2.5 py-1 bg-[#12121a] border border-gray-700 hover:border-blue-500/40 text-gray-400 hover:text-blue-400 rounded transition"
                          >
                            Modifica
                          </button>
                          <button
                            onClick={() => handleDeleteAddress(a.id)}
                            data-testid={`delete-address-${a.id}`}
                            className="text-xs px-2.5 py-1 bg-[#12121a] border border-gray-700 hover:border-red-500/40 text-gray-400 hover:text-red-400 rounded transition"
                          >
                            Elimina
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Address form modal */}
                {addressForm && (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setAddressForm(null)}>
                    <div className="bg-[#12121a] border border-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                      <div className="sticky top-0 bg-[#12121a] border-b border-gray-800 p-4 flex justify-between items-center">
                        <h3 className="text-lg font-bold text-white">{addressForm.id ? 'Modifica indirizzo' : 'Nuovo indirizzo'}</h3>
                        <button onClick={() => setAddressForm(null)} className="text-gray-500 hover:text-white text-2xl">×</button>
                      </div>
                      <form onSubmit={handleSaveAddress} className="p-5 space-y-3" data-testid="address-form-modal">
                        <input
                          type="text"
                          placeholder="Etichetta (es. Casa, Ufficio)"
                          required
                          value={addressForm.label}
                          onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                          data-testid="addr-label-input"
                          className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          placeholder="Nome e Cognome"
                          required
                          value={addressForm.full_name}
                          onChange={(e) => setAddressForm({ ...addressForm, full_name: e.target.value })}
                          data-testid="addr-name-input"
                          className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                        />
                        <input
                          type="tel"
                          placeholder="Telefono (opzionale)"
                          value={addressForm.phone || ''}
                          onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                          data-testid="addr-phone-input"
                          className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          placeholder="Indirizzo (via, numero civico)"
                          required
                          value={addressForm.address}
                          onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                          data-testid="addr-street-input"
                          className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Città"
                            required
                            value={addressForm.city}
                            onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                            data-testid="addr-city-input"
                            className="px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                          />
                          <input
                            type="text"
                            placeholder="CAP"
                            required
                            value={addressForm.zip_code}
                            onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })}
                            data-testid="addr-zip-input"
                            className="px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Provincia (es. NA)"
                            value={addressForm.province || ''}
                            onChange={(e) => setAddressForm({ ...addressForm, province: e.target.value })}
                            data-testid="addr-province-input"
                            className="px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                          />
                          <input
                            type="text"
                            placeholder="Paese"
                            required
                            value={addressForm.country}
                            onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                            data-testid="addr-country-input"
                            className="px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                          />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={!!addressForm.is_default}
                            onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
                            data-testid="addr-default-checkbox"
                            className="w-4 h-4 accent-amber-500"
                          />
                          Imposta come indirizzo predefinito
                        </label>
                        <div className="flex gap-2 pt-2">
                          <button
                            type="submit"
                            disabled={saving}
                            data-testid="addr-save-btn"
                            className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg disabled:opacity-60"
                          >
                            {saving ? 'Salvataggio...' : 'Salva indirizzo'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setAddressForm(null)}
                            className="px-4 py-2.5 border border-gray-700 text-gray-400 hover:text-white rounded-lg"
                          >
                            Annulla
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">I miei ordini</h2>
                {orders.length === 0 ? (
                  <div className="text-center py-12" data-testid="customer-orders-empty">
                    <ShoppingBag className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Non hai ancora effettuato ordini.</p>
                    <a href="/prodotti" className="inline-block px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg">
                      Inizia a comprare
                    </a>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="customer-orders-list">
                    {orders.map((order) => (
                      <button
                        key={order.id}
                        type="button"
                        onClick={() => setSelectedOrder(order)}
                        data-testid={`order-row-${order.id}`}
                        className="block w-full text-left bg-[#0a0a0f] border border-gray-800 hover:border-amber-500/40 rounded-lg p-4 transition cursor-pointer"
                      >
                        <div className="flex justify-between items-start flex-wrap gap-2 mb-3">
                          <div>
                            <div className="text-xs text-gray-500">Ordine #{(order.id || '').slice(0, 8).toUpperCase()}</div>
                            <div className="text-sm text-gray-400 mt-0.5">{order.order_date ? new Date(order.order_date).toLocaleDateString('it-IT', { day:'2-digit', month:'long', year:'numeric' }) : ''}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-amber-400 font-bold text-lg">{(order.total_amount || 0).toFixed(2)} €</div>
                            <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                              order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                              order.status === 'shipped' || order.status === 'partially_shipped' ? 'bg-blue-500/20 text-blue-400' :
                              order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {order.status === 'pending' ? 'In attesa' :
                               order.status === 'confirmed' ? 'Confermato' :
                               order.status === 'shipped' ? 'Spedito' :
                               order.status === 'partially_shipped' ? 'Spedito in parte' :
                               order.status === 'delivered' ? 'Consegnato' :
                               order.status === 'cancelled' ? 'Annullato' : order.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-400 border-t border-gray-800 pt-3">
                          <div className="mb-1"><span className="text-gray-500">Articoli:</span> {(order.items || []).map(i => `${i.quantity}× ${i.title}`).join(', ')}</div>
                          {order.tracking_number && (
                            <div><span className="text-gray-500">Tracking:</span> <span className="text-amber-400">{order.tracking_number}</span></div>
                          )}
                          <div className="text-amber-400 text-xs mt-2">Vedi dettaglio →</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'wishlist' && (
              <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">I miei preferiti</h2>
                {wishlist.length === 0 ? (
                  <div className="text-center py-12" data-testid="wishlist-empty">
                    <Heart className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Non hai ancora aggiunto prodotti ai preferiti.</p>
                    <p className="text-gray-600 text-sm mb-6">Tocca l'icona del cuore su un prodotto per aggiungerlo qui e tenere traccia dei pezzi che ti interessano.</p>
                    <a href="/prodotti" className="inline-block px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg">
                      Sfoglia i prodotti
                    </a>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="wishlist-list">
                    {wishlist.map((item) => {
                      const a = item.article || {};
                      const priceWithIva = (a.price || 0) * (1 + (parseFloat(a.iva_percentage) || 0) / 100);
                      const imgSrc = (a.images_url && a.images_url[0]) || (a.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${a.photo_url}` : null);
                      return (
                        <div key={item.article_id} className="bg-[#0a0a0f] border border-gray-800 rounded-lg overflow-hidden" data-testid={`wishlist-item-${item.article_id}`}>
                          {imgSrc && (
                            <div className="aspect-square bg-white">
                              <img src={imgSrc} alt={a.title} className="w-full h-full object-contain p-2" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                          <div className="p-3">
                            <div className="text-white text-sm font-medium line-clamp-2 mb-2 min-h-[40px]">{a.title}</div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-amber-400 font-bold">{priceWithIva.toFixed(2)} €</span>
                              {a.quantity_stock > 0 ? (
                                <span className="text-green-500 text-xs">Disponibile</span>
                              ) : a.availability_type === 'preorder' ? (
                                <span className="text-blue-400 text-xs">Preordine</span>
                              ) : (
                                <span className="text-red-400 text-xs">Esaurito</span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <a
                                href={`/prodotto/${item.article?.slug || item.article_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`wishlist-view-${item.article_id}`}
                                className="flex-1 text-center px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold rounded transition"
                              >
                                Vedi prodotto ↗
                              </a>
                              <button
                                onClick={() => removeFromWishlist(item.article_id)}
                                data-testid={`wishlist-remove-${item.article_id}`}
                                className="px-3 py-1.5 bg-[#12121a] border border-gray-700 hover:border-red-500/40 text-gray-400 hover:text-red-400 text-xs rounded transition"
                                title="Rimuovi dai preferiti"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'referral' && (
              <div data-testid="account-referral-panel" className="bg-[#12121a] border border-gray-800 rounded-xl p-6 space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Gift className="w-5 h-5 text-amber-400" /> Invita un amico
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Condividi il tuo codice. Quando un amico si registra usando il tuo codice, <b>tu ricevi un coupon spedizione gratis</b> e anche lui riceve il suo benvenuto.
                  </p>
                </div>

                {/* Referral code box */}
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/30 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-wider text-amber-300 font-semibold mb-2">Il tuo codice</p>
                  <div className="flex flex-col sm:flex-row items-stretch gap-2">
                    <code className="flex-1 bg-[#0a0a0f] border border-gray-700 rounded-lg px-4 py-3 text-amber-200 font-mono text-2xl tracking-widest text-center select-all">
                      {referralData?.referral_code || '——————'}
                    </code>
                    <button
                      type="button"
                      data-testid="referral-copy-code"
                      onClick={async () => {
                        if (!referralData?.referral_code) return;
                        try {
                          await navigator.clipboard.writeText(referralData.referral_code);
                          setRefCopied(true);
                          setTimeout(() => setRefCopied(false), 2000);
                        } catch (e) { /* ignore */ }
                      }}
                      className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg px-4 py-3 flex items-center gap-2 justify-center transition"
                    >
                      {refCopied ? <><Check className="w-4 h-4" /> Copiato!</> : <><Copy className="w-4 h-4" /> Copia codice</>}
                    </button>
                  </div>
                  {referralData?.share_url && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-400 mb-1">Link diretto da condividere:</p>
                      <input
                        readOnly
                        data-testid="referral-share-url"
                        value={referralData.share_url}
                        onClick={(e) => e.target.select()}
                        className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-xs font-mono"
                      />
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(`Ehi! Ti consiglio Edicola Cerasuolo. Iscriviti con il mio codice e ricevi spedizione gratis: ${referralData.share_url}`)}`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid="referral-share-whatsapp"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg"
                        >
                          📱 WhatsApp
                        </a>
                        <a
                          href={`mailto:?subject=${encodeURIComponent('Ti consiglio Edicola Cerasuolo')}&body=${encodeURIComponent(`Ciao! Ho scoperto un sito di modellismo e Pokemon TCG che ti consiglio. Iscriviti con il mio codice e ricevi spedizione GRATIS sul primo ordine:\n\n${referralData.share_url}`)}`}
                          data-testid="referral-share-email"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg"
                        >
                          ✉️ Email
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-3">
                  <div data-testid="referral-stat-invited" className="bg-[#0a0a0f] border border-gray-800 rounded-lg p-3 text-center">
                    <p className="text-3xl font-bold text-white">{referralData?.referral_count || 0}</p>
                    <p className="text-[11px] text-gray-400 uppercase mt-1">Amici invitati</p>
                  </div>
                  <div data-testid="referral-stat-earned" className="bg-[#0a0a0f] border border-gray-800 rounded-lg p-3 text-center">
                    <p className="text-3xl font-bold text-amber-400">{referralData?.rewards_earned || 0}</p>
                    <p className="text-[11px] text-gray-400 uppercase mt-1">Coupon guadagnati</p>
                  </div>
                  <div data-testid="referral-stat-pending" className="bg-[#0a0a0f] border border-gray-800 rounded-lg p-3 text-center">
                    <p className="text-3xl font-bold text-green-400">{referralData?.rewards_pending || 0}</p>
                    <p className="text-[11px] text-gray-400 uppercase mt-1">Da utilizzare</p>
                  </div>
                </div>

                <div className="text-xs text-gray-500 leading-relaxed pt-3 border-t border-gray-800">
                  <p className="mb-1"><b className="text-gray-300">Come funziona:</b></p>
                  <ol className="list-decimal list-inside space-y-0.5 ml-2">
                    <li>Condividi il tuo codice con i tuoi amici</li>
                    <li>Quando si registrano usando il tuo codice, riceveranno la spedizione gratis sul primo ordine</li>
                    <li>Anche tu ricevi un coupon <code className="bg-gray-800 px-1 rounded">AMICO-XXX</code> con spedizione gratuita (valido 60 giorni)</li>
                  </ol>
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <CustomerChangePassword setMessage={setMessage} setError={setError} />
            )}
          </main>
        </div>
      </div>

      {/* Order detail modal */}
      <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
    </div>
  );
};

// Change password sub-component
const CustomerChangePassword = ({ setMessage, setError }) => {
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(""); setError("");
    if (newPwd !== confirm) { setError("Le nuove password non coincidono"); return; }
    if (newPwd.length < 6) { setError("La nuova password deve avere almeno 6 caratteri"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('current_password', current);
      fd.append('new_password', newPwd);
      await axios.post(`${API}/customer/change-password`, fd);
      setMessage("Password aggiornata con successo");
      setCurrent(""); setNewPwd(""); setConfirm("");
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore aggiornamento password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#12121a] border border-gray-800 rounded-xl p-6">
      <h2 className="text-xl font-semibold text-white mb-4">Cambia password</h2>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md" data-testid="password-form">
        <input type="password" required placeholder="Password attuale" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pwd-current-input" className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none" />
        <input type="password" required placeholder="Nuova password (min. 6 caratteri)" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="pwd-new-input" className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none" />
        <input type="password" required placeholder="Conferma nuova password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pwd-confirm-input" className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none" />
        <button type="submit" disabled={saving} data-testid="pwd-save-btn" className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg disabled:opacity-60">
          {saving ? 'Aggiornamento...' : 'Aggiorna password'}
        </button>
      </form>
    </div>
  );
};



// ============== PASSWORD RESET PAGES ==============

